import { Field, Formik, FormikHelpers, FormikProps } from 'formik';
import * as AuthSelectors from 'store/modules/auth/selectors';
import Editor from 'pureUi/Editor';
import React, { useCallback, useState } from 'react';
import FluidButton from 'ui/FluidButton';
import { useSelector } from 'react-redux';
import { useBackendUsers } from 'services/BackendApi/queries/useBackendUsers';
import classnames from 'classnames';
import { userToOption } from 'ui/SingleSelect/helpers';
import SingleSelect, { ISingleSelectOption } from 'ui/SingleSelect';
import { useResponsive } from 'containers/CRM/hooks/useResponsive';
import { ErrorBar, LoadingBar } from 'ui/NetworkStatusBar';
import { companyMainInfoValidationSchema, ICrmCompanyMainInfoFormValues } from './crmCompanyMainInfoForm';
import { useUpdateCompanyMutation } from 'services/CrmApi/mutations/useUpdateCompanyMutation';
import { IUpdateCompanyRequest } from 'services/CrmApi/types/CrmCompanyTypes';
import { useNotifications } from 'hooks/useNotifications';
import { useQueryClient } from '@tanstack/react-query';
import { useCrmDirectoryEntry } from 'services/CrmApi/queries/useCrmDirectoryEntry';
import { LeaveWithoutSavingModal } from 'ui/LeaveWithoutSavingModal';
import { isDirtyEditCompanyProfileSelector } from 'store/modules/crm/selectors';
import { VerticalSpace } from 'ui/VerticalSpace';
import { ILinkedDirectoryEntryArrayItem } from 'services/CrmApi/types/DirectoryEntry';
import { CollapsibleHeader } from 'containers/CRM/components/CollapsibleHeader/CollapsibleHeader';
import { LinkedContactsAdd } from '../LinkedContactsAdd/LinkedContactsAdd';
import { LinkedContactsList } from '../LinkedContactsList/LinkedContactsList';
import { useHistory } from 'react-router-dom';
import { ModalV1 } from 'ui/ModalV1';
import { AddLead } from 'containers/CRM/components/AddLead/AddLead';
import ConfirmationModal, { EConfirmationModalType } from 'ui/ConfirmationModal';
import { useRemoveLeadMutation } from 'services/CrmApi/mutations/useRemoveLeadMutation';
import { useDefaultOnErrorHandler } from 'hooks/useDefaultOnErrorHandler';
import { CompanyMainInfoRightPart } from './CompanyMainInfoRightPart';
import { LinkedCompaniesList } from 'containers/CRM/editContact/LinkedCompaniesList/LinkedCompaniesList';
import { LinkedCompaniesAdd } from 'containers/CRM/editContact/LinkedCompaniesAdd/LinkedCompaniesAdd';
import { CategoryTag } from 'ui/CategoryDropdown/CategoryDropdown';
import { useCanEditByTypeCode } from 'containers/CRM/hooks/useCanEdit';
import { ECrmDirectoryEntryType, ICrmCompanyProfile } from 'services/CrmApi/types/DirectoryEntry';

interface ICrmCompanyMainInfoProps {
  directoryEntryId: string;
  contacts: ILinkedDirectoryEntryArrayItem[];
  companies: ILinkedDirectoryEntryArrayItem[];
  onLinkOrUnlink: () => void;
}

export const CrmCompanyMainInfo: React.FC<ICrmCompanyMainInfoProps> = ({ directoryEntryId, contacts, companies, onLinkOrUnlink }) => {
  const isDirtyEditCompanyProfile = useSelector(isDirtyEditCompanyProfileSelector);
  const { isMobile, isTablet, isDesktop, isDesktopHD } = useResponsive();
  const history = useHistory();
  
  const { showSuccessNotification } = useNotifications();
  const { defaultOnErrorHandler } = useDefaultOnErrorHandler();
  const isSr = useSelector(AuthSelectors.isSr);
  const isSales = useSelector(AuthSelectors.isSales);
  const beUsers = useBackendUsers('');
  const managerOptions: ISingleSelectOption[] = beUsers?.data?.data?.data
    ?.map(user => userToOption(user)) ?? [];

  const { crmDirectoryEntryResponse, directoryEntry } = useCrmDirectoryEntry(directoryEntryId);
  const updateCompanyMutation = useUpdateCompanyMutation();
  const isSaving = updateCompanyMutation.isPending;
  const removeLeadMutation = useRemoveLeadMutation();
  const [isRemovingLead, setIsRemovingLead] = useState(false);

  const queryClient = useQueryClient();
  const isLoading = queryClient.isFetching({ queryKey: ['crm-directory-entry', directoryEntryId] }) > 0;
  
  const companyTypeCode = directoryEntry && 'companyType' in directoryEntry.profile
    ? (directoryEntry.profile as ICrmCompanyProfile).companyType.code
    : '';
  const canEdit = useCanEditByTypeCode(companyTypeCode, ECrmDirectoryEntryType.COMPANY);

  const [openRedirectModal, setOpenRedirectModal] = useState<boolean>(false);
  const [isRemoveLeadModalOpen, setRemoveLeadModalOpen] = useState(false);

  const [isLeadShown, setLeadShown] = useState(!!directoryEntry?.lead);
  const handleShowLead = useCallback(() => {
    setLeadShown(true);
  }, []);
  const handleHideLead = useCallback(() => {
    setLeadShown(false);
  }, []);
  const handleRemoveLead = useCallback(() => {
    setRemoveLeadModalOpen(true);
  }, []);
  const handleCloseRemoveLeadModal = useCallback(() => {
    setRemoveLeadModalOpen(false);
  }, []);
  const handleConfirmRemoveLeadModal = useCallback(async () => {
    if (!directoryEntry?.lead?.id) {
      return;
    }

    try {
      setIsRemovingLead(true);
      await removeLeadMutation.mutateAsync(directoryEntry?.lead?.id);
      showSuccessNotification('Lead successfully deleted.');      
      setLeadShown(false);
      setRemoveLeadModalOpen(false);
    } catch (error) {
      defaultOnErrorHandler(error, 'Failed to update a lead.');
    } finally {
      setIsRemovingLead(false);
      await queryClient.invalidateQueries({ queryKey: ['crm-directory-entry', directoryEntryId] });
    }
  }, [defaultOnErrorHandler, directoryEntry?.lead?.id, directoryEntryId, queryClient, removeLeadMutation, showSuccessNotification]);

  const initialValues: ICrmCompanyMainInfoFormValues = {
      managerId: directoryEntry?.manager?.id ?? null,
      keyNotes: directoryEntry?.keyNotes ?? '',
  };

  const handleCloseRedirectModal = useCallback( () => {
    history.push('/crm');
  }, [history]);

  const handleFormSubmit = useCallback(async (values: ICrmCompanyMainInfoFormValues, formikHelpers: FormikHelpers<ICrmCompanyMainInfoFormValues>) => {
    const requestData : IUpdateCompanyRequest = {
      ...values,
      directoryId: directoryEntryId,
    }

    try {
      await updateCompanyMutation.mutateAsync(requestData);
      showSuccessNotification('Company updated.');      
      await queryClient.invalidateQueries({ queryKey: ['crm-directory-entry', directoryEntryId] });
      if(initialValues.managerId !== values.managerId && isSales) {
        setOpenRedirectModal(true);
      }
    } catch (error) {
      await queryClient.invalidateQueries({ queryKey: ['crm-directory-entry', directoryEntryId] });
      defaultOnErrorHandler(error, 'Failed to update a company');
    }
  }, [defaultOnErrorHandler, directoryEntryId, initialValues.managerId, isSales, queryClient, showSuccessNotification, updateCompanyMutation]);

  if (beUsers.isPending || crmDirectoryEntryResponse.isPending) {
    return (
      <div className="w-full flex justify-center mt-[20px]">
        <LoadingBar />
      </div>
    )
  }

  if (!directoryEntry || !('companyType' in directoryEntry.profile)) {
    return (
      <div className="mt-[20px]">
        <ErrorBar message="It seems this is not a company profile" />
      </div>
    )
  }

  const mainInfoContainer = classnames('main-info-container relative w-full pt-[15px]', {
    'px-[10px]': isTablet || isDesktop || isDesktopHD,
  })

  const titleLead = (
    <p className="font-pt-sans text-[17px] text-black m-0">
      <span className="mr-[5px] font-bold">Lead</span>
      {!!directoryEntry?.lead && <span>(1)</span>}
    </p>
  );

  const titleContacts = (
    <p className="font-pt-sans text-[17px] text-black m-0">
      <span className="mr-[5px] font-bold">Contacts</span>
      <span>({contacts?.length ?? 0})</span>
    </p>
  );

  const titleCompanies = (
    <p className="font-pt-sans text-[17px] text-black m-0">
      <span className="mr-[5px] font-bold">Companies</span>
      <span>({companies?.length ?? 0})</span>
    </p>
  );

  const managerElementWidth = isMobile ? 'w-[176px]' : isTablet ? 'w-[330px]' : 'w-[361px]';
  
  return (
    <div className={mainInfoContainer}>
      {openRedirectModal && (
        <ModalV1
          className="redirect-modal min-w-[500px] max-w-[500px]"
          title="You are being redirected"
          message="Data have been updated successfully, but as you changed the Manager you no longer have access to this resource"
          onClose={handleCloseRedirectModal}
        />
      )}
      <CompanyMainInfoRightPart directoryEntry={directoryEntry} />
      <h3 className="section-title font-pt-sans text-[17px] leading-normal text-black font-[700] m-0 inline-block">
        Main Info
      </h3>
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={companyMainInfoValidationSchema}
        onSubmit={handleFormSubmit}
      >
        {(form: FormikProps<ICrmCompanyMainInfoFormValues>) => {
          return (
            <div className="form-container flex flex-col justify-between gap-[20px] mt-[20px] ">
              <LeaveWithoutSavingModal
                when={form.dirty || isDirtyEditCompanyProfile}
                confirmButtonLabel="Yes"
                cancelButtonLabel="No"
              />
              <Field name="managerId">
                {({ field: { name, value }, form: { setFieldValue } }) => (
                  <SingleSelect
                    fieldId="manager"
                    label="Manager/Owner *"
                    className={`manager ${managerElementWidth}`}
                    value={value}
                    showSelectedOption
                    maxVisibleItems={isMobile ? 4 : 6}
                    scrollToSelectedItem
                    options={managerOptions ?? []}
                    onChange={value => {
                      setFieldValue(name, value);
                    }}
                    disabled={!canEdit || isLoading || isSaving}
                    errorMessage={form.touched.managerId && form.errors.managerId ? form.errors.managerId : null}
                    errorClassName="manager-error"
                  />
                )}
              </Field>
              {Array.isArray(directoryEntry.tags) && directoryEntry.tags.length > 0 && (
                <div className="crm-tags">
                  <div className="flex flex-col flex-1 relative ">
                    <label htmlFor="tags" className="font-pt-sans mb-5px text-black text-13px leading-17px tracking-2xs">
                      Tags
                    </label>
                    
                    <div className="flex flex-wrap gap-[10px] flex-1">
                      {directoryEntry.tags.map(category => (
                        <CategoryTag key={category.id} id={category.id} name={category.name} />
                      ))}
                    </div>
                  </div>
                </div>
              )}
              <div className="key-notes flex  gap-[20px]">
                <Field name="keyNotes">
                  {({ field: { name, value }, form: { setFieldValue } }) => (
                    <div className="flex flex-col w-full relative">
                      <label className="font-pt-sans mb-5px text-black text-13px leading-17px tracking-2xs">
                        Key Notes
                      </label>
                      <Editor text={value} handleEditorChange={(value) => setFieldValue(name, value)} options={[]} mentionAbility={false} disabled={!canEdit || isLoading || isSaving} />
                    </div>
                  )}
                </Field>
              </div>

              <div className="buttons-container ">
                <FluidButton type="primary" textClassName="flex items-center gap-[10px]" isLoading={isLoading || isSaving} disabled={!canEdit} onClick={form.submitForm}>
                    Save
                </FluidButton>
              </div>
            </div>
          );
        }}
      </Formik>
      <VerticalSpace height="40px" />
      <CollapsibleHeader title={titleLead}>
        {!isLeadShown && (
          <FluidButton type="secondary" textClassName="flex items-center gap-[10px]" disabled={!canEdit} onClick={handleShowLead}>
            Add
          </FluidButton>
        )}
        {isLeadShown && (
          <AddLead 
            directoryEntryId={directoryEntryId}
            lead={directoryEntry.lead}
            onCancel={handleHideLead}
            canEdit={canEdit}
            onRemove={handleRemoveLead} 
          />
        )}
      </CollapsibleHeader>
      <VerticalSpace height="40px" />
      <CollapsibleHeader title={titleContacts}>
        <LinkedContactsList contacts={contacts} onContactUnlink={onLinkOrUnlink} />
        {!isSr && <LinkedContactsAdd 
          directoryEntryId={directoryEntryId}
          onContactLink={onLinkOrUnlink}
        />}
      </CollapsibleHeader>
      <VerticalSpace height="40px" />
      <CollapsibleHeader title={titleCompanies}>
        <LinkedCompaniesList 
          companies={companies} 
          onCompanyUnlink={onLinkOrUnlink}
        />
        {!isSr && <LinkedCompaniesAdd
          directoryEntryId={directoryEntryId}
          onCompanyLink={onLinkOrUnlink}
          contactCountryCode={directoryEntry?.country?.code || null}
          isTaWithTc={false}
        />}
      </CollapsibleHeader>

      {isRemoveLeadModalOpen && (
        <ConfirmationModal
          type={EConfirmationModalType.WARNING}
          className="confirm-remove-lead-modal"
          title="You are going to remove a Lead."
          message="Are you sure?"
          confirmButtonLabel="Yes"
          cancelButtonLabel="No"
          onConfirm={handleConfirmRemoveLeadModal}
          onCancel={handleCloseRemoveLeadModal}
          isConfirmLoading={isRemovingLead}
        />
      )}
    </div>
  );
}
